"use client";

import { LocaleContext } from "@/app/i18n/client";
import { type LocaleName } from "@/app/i18n/settings";
import { type Locale } from "date-fns";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

function getLocale(lng: LocaleName): Promise<Locale> {
  switch (lng) {
    case "en":
      return import("date-fns/locale/en-US").then((x) => x.enUS);
    case "de":
      return import("date-fns/locale/de").then((x) => x.de);
    case "es":
      return import("date-fns/locale/es").then((x) => x.es);
  }
}

const DateLocaleContext = createContext<{
  locale: Locale | null;
} | null>(null);

export function DateLocaleProvider({ children }: { children: ReactNode }) {
  const localeCode = useContext(LocaleContext);
  const [locale, setLocale] = useState<Locale | null>(null);

  useEffect(() => {
    const loadAndSetLocale = async () => {
      try {
        const loadedLocale = await getLocale(localeCode);
        setLocale(loadedLocale);
      } catch (err) {
        console.error("Locale loading error:", err);
      }
    };

    loadAndSetLocale();
  }, [localeCode]);

  const value = useMemo(() => ({ locale }), [locale]);

  return (
    <DateLocaleContext.Provider value={value}>
      {children}
    </DateLocaleContext.Provider>
  );
}

export function useDateLocale() {
  const context = useContext(DateLocaleContext);
  if (!context) {
    throw new Error("useDateLocale must be used within a DateLocaleProvider");
  }
  return context.locale ?? undefined;
}
